// packages/web-ui/src/components/mention-helper/MentionPopup.tsx
//
// Story W4-S6 — React `<MentionPopup>` for the SPA chat composer's
// `@`-mention autocomplete. Builds on the rank helper in
// `./rank-mentions.ts` (drop-in replaceable with `rankCandidates` from
// `@tessa-lean/base` once the base lift lands). Selectors / data-testids
// match the W2-S7 + W4-S6 contract documented in the story file
// (`_bmad-output/implementation-artifacts/4-6-...md` AC #6 §"Selector
// contract" and UX §8 mention autocomplete pattern).
//
// Surface (W2-S7 AC #3-4 / W4-S6 AC #6):
//   - root: `[data-testid="mention-popup"]`, role=listbox, ARIA-labelled
//   - row: `[data-testid="mention-popup-row"][data-active="true"]` for active
//   - filter chips: `[data-testid="mention-filter-chip"][data-kind=...]`
//   - keyboard: ↑/↓ navigate, Enter/Tab insert, Esc dismiss
//   - click-outside: dismiss event
//
// The popup is *controlled* — the parent (Composer) owns the prefix text
// and the open/close gate. The popup never owns the textarea or the
// debounce timer; that lives in `useMentionAutocomplete`.

import type { JSX, KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import type {
  KbEntityKind,
  MentionCandidate,
} from '@tessa-lean/base/api-schemas/index.js';

/** Filter-chip definition. `kind === undefined` means "All". */
interface FilterChip {
  label: string;
  kind: KbEntityKind | undefined;
  testId: string;
}

const FILTER_CHIPS: ReadonlyArray<FilterChip> = [
  { label: 'All', kind: undefined, testId: 'mention-filter-all' },
  { label: 'Parts', kind: 'part', testId: 'mention-filter-part' },
  { label: 'Ports', kind: 'port', testId: 'mention-filter-port' },
  { label: 'IF', kind: 'interface', testId: 'mention-filter-interface' },
  { label: 'Conn', kind: 'connection', testId: 'mention-filter-connection' },
  { label: 'Val', kind: 'value-attribute', testId: 'mention-filter-value-attribute' },
];

const KIND_ICON: Readonly<Record<KbEntityKind, string>> = Object.freeze({
  part: '\u{1F537}', // 🔷
  'part-usage': '\u{1F539}', // 🔹 (specs — value-bound part usages)
  port: '◆', // ◆
  interface: '\u{1F50C}', // 🔌
  'interface-def': '\u{1F50C}', // 🔌 (F1: shares the family glyph)
  connection: '\u{1F517}', // 🔗
  'connection-def': '\u{1F517}', // 🔗 (F1: shares the family glyph)
  'value-attribute': 'ƒ', // ƒ
});

export interface MentionPopupProps {
  /** Ranked candidates supplied by `useMentionAutocomplete`. */
  candidates: ReadonlyArray<MentionCandidate>;
  /** When `false` the popup renders nothing — controlled by the parent. */
  open: boolean;
  /** Active row index (0-based). Controlled — parent updates on ↑/↓. */
  activeIndex: number;
  /** Active kind filter (undefined = All). Controlled. */
  activeFilter: KbEntityKind | undefined;
  /** The query (prefix text after `@`). Drives empty-state copy. */
  prefix: string;
  /** Fires when the user picks a row (Enter / Tab / click). */
  onSelect: (candidate: MentionCandidate) => void;
  /** Fires on Esc, click-outside, or empty filtered list + Enter. */
  onDismiss: () => void;
  /** Fires when ↑/↓ moves the active row (parent updates `activeIndex`). */
  onActiveIndexChange: (index: number) => void;
  /** Fires when a filter chip is clicked or `Ctrl+1..6` is pressed. */
  onFilterChange: (kind: KbEntityKind | undefined) => void;
  /** Override for tests — root id used as `aria-controls` from the textarea. */
  popupId?: string;
}

const DEFAULT_POPUP_ID = 'tessa-mention-popup';

function filterByKind(
  candidates: ReadonlyArray<MentionCandidate>,
  kind: KbEntityKind | undefined,
): ReadonlyArray<MentionCandidate> {
  if (kind === undefined) return candidates;
  return candidates.filter((c) => c.kind === kind);
}

/**
 * Floating mention-autocomplete listbox. Renders kind-filter chips, a
 * scrollable result list, and an empty-state. Keyboard handling is split:
 * the textarea forwards the event via `handleKeyDown` (exposed on the
 * imperative ref) so the list can scroll without stealing focus from the
 * composer.
 */
export function MentionPopup(props: MentionPopupProps): JSX.Element | null {
  const {
    candidates,
    open,
    activeIndex,
    activeFilter,
    prefix,
    onSelect,
    onDismiss,
    onActiveIndexChange,
    onFilterChange,
    popupId = DEFAULT_POPUP_ID,
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const filtered = useMemo(
    () => filterByKind(candidates, activeFilter),
    [candidates, activeFilter],
  );

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event: MouseEvent): void => {
      const root = rootRef.current;
      if (root === null) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      onDismiss();
    };
    document.addEventListener('mousedown', handler);
    return (): void => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open, onDismiss]);

  if (!open) return null;

  const handleRowClick = (candidate: MentionCandidate): void => {
    onSelect(candidate);
  };

  const clamp = (index: number, len: number): number => {
    if (len === 0) return 0;
    if (index < 0) return 0;
    if (index >= len) return len - 1;
    return index;
  };

  // Keyboard handling on the popup root — used when the popup itself takes focus
  // (rare; mostly the composer forwards events). Mirrors the contract from
  // UX §8.5.
  const onRootKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onActiveIndexChange(clamp(activeIndex + 1, filtered.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onActiveIndexChange(clamp(activeIndex - 1, filtered.length));
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const candidate = filtered[clamp(activeIndex, filtered.length)];
      if (candidate !== undefined) {
        event.preventDefault();
        onSelect(candidate);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onDismiss();
      return;
    }
    // Ctrl+1..6 — kind filter chips per UX §8.5.
    if (event.ctrlKey && /^[1-6]$/.test(event.key)) {
      event.preventDefault();
      const idx = Number.parseInt(event.key, 10) - 1;
      const chip = FILTER_CHIPS[idx];
      if (chip !== undefined) {
        onFilterChange(chip.kind);
      }
    }
  };

  return (
    <div
      ref={rootRef}
      id={popupId}
      role="listbox"
      aria-label="Mention suggestions"
      data-testid="mention-popup"
      data-prefix={prefix}
      tabIndex={-1}
      onKeyDown={onRootKeyDown}
      className={[
        'flex flex-col rounded-none border border-taupe-500 bg-cream-50',
        'min-w-[260px] max-w-[420px] max-h-[320px] text-[13px] outline-none',
      ].join(' ')}
    >
      <div
        data-testid="mention-popup-filters"
        role="toolbar"
        aria-label="Filter by kind"
        className="flex flex-wrap gap-1 border-b border-taupe-300 px-2 py-1"
      >
        {FILTER_CHIPS.map((chip) => {
          const active = chip.kind === activeFilter;
          return (
            <button
              key={chip.label}
              type="button"
              data-testid={chip.testId}
              data-kind={chip.kind ?? 'all'}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              onClick={() => {
                onFilterChange(chip.kind);
              }}
              className={[
                'rounded-none px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                active
                  ? 'bg-taupe-900 text-white'
                  : 'border border-taupe-500 text-fg hover:border-audi-red hover:text-audi-red',
              ].join(' ')}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div
          data-testid="mention-popup-empty"
          className="px-3 py-2 text-xs text-taupe-700"
        >
          No matching elements
        </div>
      ) : (
        <ul
          data-testid="mention-popup-list"
          className="flex flex-col overflow-y-auto"
          role="presentation"
        >
          {filtered.map((candidate, idx) => {
            const isActive = idx === activeIndex;
            return (
              <li
                key={candidate.entityId}
                role="option"
                aria-selected={isActive}
                data-testid="mention-popup-row"
                data-entity-id={candidate.entityId}
                data-kind={candidate.kind}
                data-active={isActive ? 'true' : 'false'}
                tabIndex={-1}
                onClick={() => {
                  handleRowClick(candidate);
                }}
                onMouseEnter={() => {
                  onActiveIndexChange(idx);
                }}
                className={[
                  'flex cursor-pointer items-center gap-2 px-3 py-1',
                  isActive
                    ? 'bg-[var(--accent-cream-active)] text-taupe-900'
                    : 'hover:bg-cream-200',
                ].join(' ')}
              >
                <span
                  aria-hidden="true"
                  data-testid="mention-popup-row-icon"
                  className="w-5 text-center"
                >
                  {KIND_ICON[candidate.kind]}
                </span>
                <span className="font-medium">{candidate.chipText}</span>
                <span className="ml-auto truncate text-[11px] text-taupe-700">
                  {candidate.qualifiedName}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Imperative keyboard handler — call this from the composer's textarea
 * `onKeyDown` so the textarea retains focus while the popup is open.
 *
 * Returns `true` when the popup consumed the event (the composer should
 * NOT forward it further).
 */
export function handleMentionPopupKey(args: {
  event: KeyboardEvent<HTMLElement>;
  open: boolean;
  candidates: ReadonlyArray<MentionCandidate>;
  activeFilter: KbEntityKind | undefined;
  activeIndex: number;
  onSelect: (candidate: MentionCandidate) => void;
  onDismiss: () => void;
  onActiveIndexChange: (index: number) => void;
  onFilterChange: (kind: KbEntityKind | undefined) => void;
}): boolean {
  if (!args.open) return false;
  const filtered = filterByKind(args.candidates, args.activeFilter);
  const { event } = args;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    const next = filtered.length === 0 ? 0 : Math.min(args.activeIndex + 1, filtered.length - 1);
    args.onActiveIndexChange(next);
    return true;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    args.onActiveIndexChange(Math.max(args.activeIndex - 1, 0));
    return true;
  }
  if (event.key === 'Enter' || event.key === 'Tab') {
    const candidate = filtered[Math.min(Math.max(args.activeIndex, 0), filtered.length - 1)];
    if (candidate !== undefined) {
      event.preventDefault();
      args.onSelect(candidate);
      return true;
    }
    return false;
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    args.onDismiss();
    return true;
  }
  if (event.ctrlKey && /^[1-6]$/.test(event.key)) {
    event.preventDefault();
    const idx = Number.parseInt(event.key, 10) - 1;
    const chip = FILTER_CHIPS[idx];
    if (chip !== undefined) {
      args.onFilterChange(chip.kind);
    }
    return true;
  }
  return false;
}

/** Test-only: re-export the icon map so tests can assert without duping. */
export const _MENTION_KIND_ICON: typeof KIND_ICON = KIND_ICON;
/** Test-only helper: enumerate filter chips. */
export const _MENTION_FILTER_CHIPS: typeof FILTER_CHIPS = FILTER_CHIPS;
